/**
 * ISO-8601 week numbering, shared by everything that talks about a week.
 *
 * `assigned_week` on a task is a bare number, so it only means anything if every screen
 * counts weeks the same way. They did not: the assign form used an approximation off
 * 1 January while the calendar and `GET /api/analyst/assignments/week` use ISO weeks, so
 * a task saved as "week 39" could be looked for in a different week entirely. This is the
 * one definition — ISO, matching `java.time.temporal.WeekFields.ISO` on the server.
 *
 * ISO rules, for the cases that bite: weeks run Monday to Sunday, week 1 is the one
 * containing the first Thursday of January, and a date in early January can belong to the
 * last week of the previous year (so a week carries its own `year`, not the date's).
 */

export interface IsoWeek {
  week: number;
  /** The ISO week-year, which is not always the calendar year of the date. */
  year: number;
}

/** Midnight UTC for a date's calendar day, so week maths never crosses a DST boundary. */
function utcDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/** Monday = 0 … Sunday = 6. */
function isoDayIndex(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

/** The ISO week and week-year a date falls in. */
export function isoWeekOf(date: Date): IsoWeek {
  // The ISO year is whichever year this week's Thursday lands in.
  const thursday = utcDay(date);
  thursday.setUTCDate(thursday.getUTCDate() - isoDayIndex(thursday) + 3);

  const year = thursday.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(year, 0, 4)); // 4 Jan is always in week 1
  firstThursday.setUTCDate(firstThursday.getUTCDate() - isoDayIndex(firstThursday) + 3);

  const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86400000));
  return { week, year };
}

/** The Monday that starts a given ISO week. */
export function mondayOf({ week, year }: IsoWeek): Date {
  const anchor = new Date(Date.UTC(year, 0, 4));
  const mondayOfWeek1 = new Date(anchor);
  mondayOfWeek1.setUTCDate(anchor.getUTCDate() - isoDayIndex(anchor));

  const monday = new Date(mondayOfWeek1);
  monday.setUTCDate(mondayOfWeek1.getUTCDate() + (week - 1) * 7);
  return monday;
}

/** Steps a week by whole weeks, rolling over the year end correctly. */
export function shiftWeek(current: IsoWeek, by: number): IsoWeek {
  const monday = mondayOf(current);
  monday.setUTCDate(monday.getUTCDate() + by * 7);
  return isoWeekOf(new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate()));
}

/** The ISO week of this moment. */
export function currentIsoWeek(): IsoWeek {
  return isoWeekOf(new Date());
}

/** `yyyy-mm-dd` for a date, which is how every date crosses the wire here. */
export function toIsoDate(date: Date): string {
  return utcDay(date).toISOString().slice(0, 10);
}

/** The seven ISO dates of a week, Monday first. */
export function daysOfWeek(weekStart: string | Date): string[] {
  const start = typeof weekStart === 'string' ? new Date(weekStart) : weekStart;
  if (Number.isNaN(start.getTime())) return [];
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return toIsoDate(day);
  });
}

/**
 * Every ISO week touched by a date range, in order.
 *
 * This is what turns a From/To range on the assign form into the week numbers the tasks
 * are stored against. A range inside one week gives one entry; a range spanning three
 * gives three. Returns an empty list when either end is missing or the range is backwards,
 * so the caller can simply say nothing is selected yet.
 *
 * Capped, because a mis-typed year would otherwise ask the form to render thousands of
 * rows — a range that long is a typo, not a plan.
 */
export const MAX_WEEK_SPAN = 52;

export function weeksBetween(from: string, to: string): IsoWeek[] {
  if (!from || !to) return [];

  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end.getTime() < start.getTime()) return [];

  const weeks: IsoWeek[] = [];
  let cursor = isoWeekOf(start);
  const last = isoWeekOf(end);

  for (let guard = 0; guard < MAX_WEEK_SPAN; guard++) {
    weeks.push(cursor);
    if (cursor.week === last.week && cursor.year === last.year) return weeks;
    cursor = shiftWeek(cursor, 1);
  }
  return weeks;
}

/** "Week 39" or "Weeks 39–41", for saying what a range came to. */
export function describeWeeks(weeks: IsoWeek[]): string {
  if (weeks.length === 0) return '';
  if (weeks.length === 1) return `Week ${weeks[0].week}`;
  return `Weeks ${weeks[0].week}–${weeks[weeks.length - 1].week}`;
}
